import { useMutation, useQuery } from '@tanstack/react-query';

import toast from 'react-hot-toast';

import { JsonApiObject } from '../../../shared/utilities/jsonApiNormalizer';

import { queryClient } from '../../../shared/providers/react-query-provider';
import { getEnvRequired } from '../../../shared/utilities/env';
import { ApiClient } from '../../shared/api/client';

export type TagAttributes = { name: string; color: string };
export interface IncludedTag extends JsonApiObject {
    type: 'tag';
    id: string;
    attributes: TagAttributes;
}

type AddTagResponse = {
    jsonapi: { version: '1.0' };
    data: IncludedTag;
};

type GetAllTagResponse = {
    jsonapi: { version: '1.0' };
    data: IncludedTag[];
};

const apiClient = new ApiClient(true);

const REQUEST_MANAGER_API_URL = getEnvRequired('REQUEST_MANAGER_API_URL');

const getTagsQueryKey = () => ['tags'];

export const useGetAllTags = () =>
    useQuery<GetAllTagResponse>({
        queryKey: getTagsQueryKey(),
        queryFn: () => {
            const url = `${REQUEST_MANAGER_API_URL}/tag`;
            return apiClient.get(url);
        },
    });

export const useCreateTag = () => {
    return useMutation<AddTagResponse, null, TagAttributes & { dossierId: number }>({
        mutationFn: (tagAttributes) => {
            const url = `${REQUEST_MANAGER_API_URL}/tag`;
            return apiClient.post(url, JSON.stringify(tagAttributes));
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({
                queryKey: getTagsQueryKey(),
            });
        },
        onError: () => {
            toast.error('dossier.notifications.error');
        },
    });
};

export const useAssignTagToDossier = () =>
    useMutation<null, null, { tagId: string; dossierId: number }>({
        mutationFn: ({ tagId, dossierId }) => {
            const url = `${REQUEST_MANAGER_API_URL}/tag/${tagId}/dossiers`;
            return apiClient.post(url, JSON.stringify({ dossierId }));
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({
                queryKey: getTagsQueryKey(),
            });
        },
        onError: () => {
            toast.error('dossier.notifications.error');
        },
    });

export const useUnassignTagToDossier = () =>
    useMutation<null, null, { tagId: string; dossierId: number }>({
        mutationFn: ({ tagId, dossierId }) => {
            const url = `${REQUEST_MANAGER_API_URL}/tag/${tagId}/dossiers/${dossierId}`;
            return apiClient.delete(url);
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({
                queryKey: getTagsQueryKey(),
            });
        },
        onError: () => {
            toast.error('dossier.notifications.error');
        },
    });
